"use client";

// NettiError — the ONE surface innernet shows when something did not load.
//
// Nobody should ever meet a framework error page in innernet. Every boundary
// (error.tsx, SafeBoundary, ChunkRecovery) ends here, in the NotFound.tsx
// language: the ground that is already there, netti's mark turning (only the
// petals; the centre cell stands), one line in lowercase, one door. It never
// blames, never shouts, and always says the true thing first: nothing in your
// memory changed, because a failed READ is not a write.
//
//   reconnecting  "one moment, we're reconnecting."      we are already trying
//   failed        "that didn't load. nothing in your memory changed. please try again."
//   updated       "innernet just updated. one moment."   a new build; a reload follows
//
// RouteError (below) is error.tsx's whole body, so each segment's error file is
// five lines: report, retry quietly (useAutoRetry), show.

import "./nettiError.css";
import { useEffect, type CSSProperties } from "react";
import OrganismSpinner from "./OrganismSpinner";
import { PillButton } from "../ui";
import { reportClientError } from "../../lib/os/reportError";
import { isChunkError, signalChunkError } from "../../lib/os/resilience";
import { useAutoRetry } from "./useAutoRetry";

export type NettiErrorState = "reconnecting" | "failed" | "updated";
export type NettiErrorVariant = "page" | "region" | "sheet";

const COPY: Record<NettiErrorState, { line: string; body?: string }> = {
  reconnecting: { line: "one moment, we’re reconnecting." },
  failed: { line: "that didn’t load.", body: "nothing in your memory changed. please try again." },
  updated: { line: "innernet just updated. one moment." },
};

export interface NettiErrorProps {
  variant?: NettiErrorVariant;
  state?: NettiErrorState;
  /** one inline row — for chrome that fails small (the navigator). */
  compact?: boolean;
  /** fixed over everything and opaque — ChunkRecovery's last word. */
  overlay?: boolean;
  /** a try is in flight: the pill shows its spinner and waits. */
  busy?: boolean;
  /** shows the door. Omit while we are still retrying on our own. */
  onRetry?: () => void;
  retryLabel?: string;
  /** page: a quiet way home. Pass null to leave it out. */
  homeHref?: string | null;
  className?: string;
  style?: CSSProperties;
}

export default function NettiError({
  variant = "region",
  state = "failed",
  compact = false,
  overlay = false,
  busy = false,
  onRetry,
  retryLabel = "try again",
  homeHref,
  className,
  style,
}: NettiErrorProps) {
  const c = COPY[state];
  const page = variant === "page";
  const Root = page ? "main" : "div";
  const Line = page ? "h1" : "p";
  const cls = ["nfe", `nfe--${variant}`, overlay ? "nfe--overlay" : "", className].filter(Boolean).join(" ");
  // a way home is for when we have stopped trying; while we are still on it, one line is enough
  const home = state !== "failed" ? null : homeHref === undefined ? (page ? "/" : null) : homeHref;

  const acts =
    onRetry || home || (!page && state === "failed") ? (
      <div className="nfe__acts">
        {onRetry && !compact ? (
          <PillButton onClick={onRetry} loading={busy} className="nfe__pill">
            {retryLabel}
          </PillButton>
        ) : null}
        {onRetry && compact ? (
          <button type="button" className="quiet-link" onClick={onRetry} disabled={busy}>
            {retryLabel}
          </button>
        ) : null}
        {home ? (
          <a className="quiet-link" href={home}>
            go home
          </a>
        ) : null}
        {!page && state === "failed" ? (
          <button type="button" className="quiet-link" onClick={() => window.location.reload()}>
            reload the page
          </button>
        ) : null}
      </div>
    ) : null;

  return (
    <Root
      className={cls}
      style={style}
      data-state={state}
      data-compact={compact || undefined}
      data-busy={busy || undefined}
    >
      <div className="nfe__stack" role={state === "failed" ? "alert" : "status"} aria-live={state === "failed" ? "assertive" : "polite"}>
        <OrganismSpinner size="1em" tone="current" className="nfe__mark" />
        <div className="nfe__say">
          <Line className="nfe__line">{c.line}</Line>
          {c.body && !compact ? <p className="nfe__body">{c.body}</p> : null}
        </div>
        {acts}
      </div>
    </Root>
  );
}

/* --------------------------------------------------------------------- error.tsx */

/**
 * The whole body of an error.tsx. A route threw: tell PostHog once, try again
 * quietly on the schedule, and meanwhile show NettiError. A failed code download
 * is a different kind of trip: it hands over to ChunkRecovery (one reload, then
 * a plain page) and shows "innernet just updated".
 */
export function RouteError({
  error,
  unstable_retry,
  where,
  variant = "page",
  rootClassName,
  rootStyle,
  homeHref,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
  where: string;
  variant?: NettiErrorVariant;
  rootClassName?: string;
  rootStyle?: CSSProperties;
  homeHref?: string | null;
}) {
  const chunk = isChunkError(error);

  useEffect(() => {
    reportClientError(error, where, { digest: error.digest, chunk });
    if (chunk) signalChunkError(error);
  }, [error, where, chunk]);

  const { phase, busy, retryNow } = useAutoRetry({ where, error, retry: unstable_retry, enabled: !chunk });
  const state: NettiErrorState = chunk ? "updated" : phase === "waiting" ? "reconnecting" : "failed";

  return (
    <NettiError
      variant={variant}
      state={state}
      busy={busy}
      onRetry={chunk ? () => window.location.reload() : phase === "gave-up" ? retryNow : undefined}
      retryLabel={chunk ? "reload" : "try again"}
      homeHref={homeHref}
      className={rootClassName}
      style={rootStyle}
    />
  );
}
